import React from "react";
import { cn } from "../../utils/cn";

/**
 * DESIGN.md badge — small status pill.
 *
 * Variants pull from the signature palette so badges stay inside the brand surface set:
 *  - default     surface-soft bg, ink text
 *  - success     mint bg, forest text
 *  - info        soft blue tinted, info text
 *  - warning     cream bg, ink text
 *  - danger      coral bg, white text
 *  - coral       coral bg, white text
 *  - forest      forest bg, white text
 *  - cream       cream bg, ink text
 *  - peach       peach bg, ink text
 *  - mint        mint bg, ink text
 *  - mustard     mustard bg, ink text
 *  - yellow      yellow bg, ink text
 *  - dark        ink bg, white text
 */
// Signature pastels keep fixed-dark text via `text-onSignature` (their bg stays light in dark mode).
// Coral/forest stay fixed-dark surfaces so `text-white` is always correct on them.
// `dark` variant uses bg-ink (auto-flips) + text-onInk (auto-flips) so it inverts cleanly.
const VARIANT_CLASSES = {
  default: "bg-surface-soft text-ink",
  success: "bg-signature-mint text-signature-forest",
  info: "bg-[#dde7fb] text-info",
  warning: "bg-signature-cream text-onSignature",
  danger: "bg-signature-coral text-white",
  coral: "bg-signature-coral text-white",
  forest: "bg-signature-forest text-white",
  cream: "bg-signature-cream text-onSignature",
  peach: "bg-signature-peach text-onSignature",
  mint: "bg-signature-mint text-onSignature",
  mustard: "bg-signature-mustard text-onSignature",
  yellow: "bg-signature-yellow text-onSignature",
  dark: "bg-ink text-onInk",
};

export function Badge({
  variant = "default",
  size = "md",
  className,
  text,
  children,
  uppercase = false,
  ...rest
}) {
  const variantClass = VARIANT_CLASSES[variant] || VARIANT_CLASSES.default;
  const sizeClass = size === "sm" ? "text-[11px] px-2 py-0.5" : "text-[12px] px-2.5 py-1";
  return (
    <span
      className={cn(
        "inline-flex items-center font-medium rounded-xs leading-tight",
        sizeClass,
        variantClass,
        uppercase && "uppercase tracking-wide",
        className
      )}
      {...rest}
    >
      {text ?? children}
    </span>
  );
}

export default Badge;
